<template>
  <!-- Mobile Hamburger Button -->
  <button
    @click="toggleMobileMenu"
    class="lg:hidden fixed top-20 left-4 z-40 p-2 bg-white rounded-lg shadow-md border border-slate-200 hover:bg-slate-50 transition-colors"
    :class="{ 'hidden': isMobileOpen }"
  >
    <PhList class="w-6 h-6 text-slate-600" />
  </button>

  <!-- Mobile Backdrop -->
  <Transition name="fade">
    <div
      v-if="isMobileOpen"
      @click="closeMobileMenu"
      class="lg:hidden fixed inset-0 bg-black/50 z-40"
    />
  </Transition>

  <!-- Sidebar -->
  <aside
    data-tour="sidebar"
    :class="[
      'fixed top-16 left-0 h-[calc(100vh-4rem)] bg-white border-r border-slate-200 z-50 transition-all duration-300 flex flex-col',
      isCollapsed ? 'w-16' : 'w-64',
      isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
    ]"
  >
    <!-- Navigation Links -->
    <nav data-tour="sidebar-nav" class="flex-1 overflow-y-auto py-4 px-2">
      <!-- Main Section -->
      <router-link
        v-for="item in mainItems"
        :key="item.name"
        :to="item.href"
        @click="closeMobileMenu"
        :class="[
          'flex items-center gap-3 px-3 py-2.5 rounded-lg mb-1 transition-all duration-200 group',
          isActive(item)
            ? 'bg-[rgba(57,255,20,0.1)] text-[#32cd32] font-medium'
            : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
        ]"
        :title="isCollapsed ? item.label : undefined"
        :data-tour="item.dataTour"
      >
        <component
          :is="item.icon"
          :class="[
            'w-5 h-5 flex-shrink-0 transition-colors',
            isActive(item) ? 'text-[#32cd32]' : 'text-slate-400 group-hover:text-slate-600'
          ]"
          :weight="isActive(item) ? 'fill' : 'regular'"
        />
        <span v-if="!isCollapsed" class="truncate">{{ item.label }}</span>
      </router-link>

      <!-- Admin Section -->
      <template v-if="isAdmin">
        <div class="my-3 mx-3 border-t border-slate-200" />
        <div v-if="!isCollapsed" class="px-3 mb-2 text-xs font-semibold text-slate-400 uppercase tracking-wider">
          Admin
        </div>
        <router-link
          v-for="item in adminItems"
          :key="item.name"
          :to="item.href"
          @click="closeMobileMenu"
          :class="[
            'flex items-center gap-3 px-3 py-2.5 rounded-lg mb-1 transition-all duration-200 group',
            isActive(item)
              ? 'bg-[rgba(57,255,20,0.1)] text-[#32cd32] font-medium'
              : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
          ]"
          :title="isCollapsed ? item.label : undefined"
        >
          <component
            :is="item.icon"
            :class="[
              'w-5 h-5 flex-shrink-0 transition-colors',
              isActive(item) ? 'text-[#32cd32]' : 'text-slate-400 group-hover:text-slate-600'
            ]"
            :weight="isActive(item) ? 'fill' : 'regular'"
          />
          <span v-if="!isCollapsed" class="truncate">{{ item.label }}</span>
        </router-link>
      </template>
    </nav>

    <!-- Collapse Toggle (Desktop only) -->
    <button
      @click="toggleCollapse"
      class="hidden lg:flex items-center justify-center p-3 border-t border-slate-100 text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition-colors"
    >
      <PhCaretLeft
        :class="[
          'w-5 h-5 transition-transform duration-300',
          isCollapsed ? 'rotate-180' : ''
        ]"
      />
    </button>
  </aside>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import {
  PhList,
  PhCaretLeft,
  PhSquaresFour,
  PhRocket,
  PhGraduationCap,
  PhChartBar,
  PhBuildings,
  PhCreditCard,
  PhGauge,
  PhUsers,
  PhPackage,
  PhRepeat,
  PhChartLineUp,
  PhFileText,
} from '@phosphor-icons/vue';

interface NavItem {
  name: string;
  label: string;
  href: string;
  icon: any;
  matchPrefix?: string;
  dataTour?: string;
}

defineProps<{
  isAdmin: boolean;
}>();

const emit = defineEmits<{
  'update:collapsed': [value: boolean];
}>();

const route = useRoute();

// State
const isCollapsed = ref(false);
const isMobileOpen = ref(false);

// Main navigation items
const mainItems: NavItem[] = [
  { name: 'Products', label: 'Products', href: '/', icon: PhRocket, dataTour: 'nav-products' },
  { name: 'Dashboard', label: 'Dashboard', href: '/dashboard', icon: PhSquaresFour, dataTour: 'nav-dashboard' },
  // { name: 'Courses', label: 'Courses', href: '/courses', icon: PhGraduationCap, dataTour: 'nav-courses' }, // Hidden until feature is ready
  { name: 'Usage', label: 'Usage', href: '/usage', icon: PhChartBar, dataTour: 'nav-usage' },
  { name: 'Organization', label: 'Organization', href: '/organization', icon: PhBuildings, dataTour: 'nav-organization' },
  { name: 'Subscription', label: 'Subscription', href: '/subscription', icon: PhCreditCard, dataTour: 'nav-subscription' },
];

// Admin navigation items
const adminItems: NavItem[] = [
  { name: 'AdminDashboard', label: 'Overview', href: '/admin', icon: PhGauge },
  { name: 'AdminCustomers', label: 'Customers', href: '/admin/customers', icon: PhUsers },
  { name: 'AdminServices', label: 'Products', href: '/admin/products', icon: PhPackage },
  { name: 'AdminSubscriptions', label: 'Subscriptions', href: '/admin/subscriptions', icon: PhRepeat },
  { name: 'AdminAnalytics', label: 'Analytics', href: '/admin/analytics', icon: PhChartLineUp },
  { name: 'AdminLogs', label: 'Audit Logs', href: '/admin/logs', icon: PhFileText },
];

// Check if route is active
function isActive(item: NavItem): boolean {
  if (item.name === 'Products') {
    return route.path === '/';
  }
  if (item.name === 'AdminDashboard') {
    return route.path === '/admin';
  }
  return route.path === item.href;
}

// Toggle collapse state
function toggleCollapse() {
  isCollapsed.value = !isCollapsed.value;
  localStorage.setItem('dashboard-sidenav-collapsed', String(isCollapsed.value));
  emit('update:collapsed', isCollapsed.value);
}

// Mobile menu functions
function toggleMobileMenu() {
  isMobileOpen.value = !isMobileOpen.value;
}

function closeMobileMenu() {
  isMobileOpen.value = false;
}

// Load collapsed state from localStorage
onMounted(() => {
  const saved = localStorage.getItem('dashboard-sidenav-collapsed');
  if (saved === 'true') {
    isCollapsed.value = true;
  }
  emit('update:collapsed', isCollapsed.value);
});

// Close mobile menu on route change
watch(() => route.path, () => {
  closeMobileMenu();
});
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
